import type { SetStateAction } from 'react';
import { useCallback, useState } from 'react';

const getStoredValue = <T>(
  key: string,
  sanitise: (raw: unknown) => T,
  session: boolean
): T => {
  const storage = session ? sessionStorage : localStorage;

  const raw = storage.getItem(key);
  if (raw === undefined || raw === null) {
    return sanitise(undefined);
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    console.warn(
      `Invalid JSON in ${session ? 'sessionStorage' : 'localStorage'} ${key}`,
      key,
      raw
    );
  }

  return sanitise(parsed);
};

/**
 * Works just like useState, but persists the value to local or session storage.
 * Values are stored as stringified JSON - the sanitise function should take an
 * unknown from localstorage and convert it to the required shape T
 */
export const usePersistedState = <T>(
  key: string,
  sanitise: (raw: unknown) => T,
  persist = false
): [T, (v: SetStateAction<T>) => void] => {
  const [value, setValue] = useState<T>(() =>
    getStoredValue(key, sanitise, !persist)
  );

  const setValueWrapped: (v: SetStateAction<T>) => void = useCallback(
    val => {
      const storage = persist ? localStorage : sessionStorage;
      const prevState = getStoredValue(key, sanitise, !persist);
      let updated;
      if (typeof val === 'function') {
        // @ts-expect-error React's types are a bit broad here
        updated = val(prevState);
      } else {
        updated = val;
      }
      storage.setItem(key, JSON.stringify(updated));
      setValue(updated);
    },
    [persist, key, sanitise]
  );

  return [value, setValueWrapped];
};
